import {
  Clock,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Printer,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import Swal from "sweetalert2";
import LoadingMessage from "../../../../components/LoadingMessage";
import { tierDisplayNames, tierIcons } from "../../../../constants/constans";
import useCycleBreakdownPrint, {
  CycleBreakdownPrintData,
} from "../../../../hooks/useCycleBreakdownPrint";
import {
  CycleFormData,
  ProductionCycle,
} from "../../../../redux/allocationService";
import {
  CreateProductionCyclesRequest,
  ProductionCycleItemRequest,
  ProductionCycleRequest,
  ProductionItem,
  useCreateProductionCycleMutation,
  useCreateProductionCyclesMutation,
  useDeleteProductionCycleMutation,
  useLazyGetProductionCycleBreakdownQuery,
} from "../../../../redux/services/allocationService";
import {
  formatDateForAPISimple,
  formatTimeFromISO,
  getCycleCellClass,
  getCycleColor,
  getCycleTotalCellClass,
  getProductTier,
  getProductUniqueKey,
} from "../../../../utility/productUtility";
import "./index.css";

// Define props interface
interface CycleBreakdownScreenProps {
  productionQuantities: Record<string, number>;
  selectedDate?: string;
  templateMode?: boolean;
  singleDateMode?: boolean;
  templateSourceDate?: string;
  templateSaveToDate?: string;
  isShowingLoadFromDate?: boolean;
  isPastDate?: boolean;
}
export function CycleBreakdownScreen({
  productionQuantities,
  selectedDate: displayDate,
  templateMode = false,
  singleDateMode = false,
  templateSourceDate,
  templateSaveToDate,
  isShowingLoadFromDate = false,
  isPastDate = false,
}: CycleBreakdownScreenProps) {
  const [
    triggerGetProductionCycleBreakdown,
    { data: apiData, isLoading, error },
  ] = useLazyGetProductionCycleBreakdownQuery();

  const [createProductionCycle, { isLoading: isCreating }] =
    useCreateProductionCycleMutation();
  const [createProductionCycles, { isLoading: isSaving }] =
    useCreateProductionCyclesMutation();
  const [deleteProductionCycle, { isLoading: isDeleting }] =
    useDeleteProductionCycleMutation();

  const { printCycleBreakdown, isPrinting } = useCycleBreakdownPrint();

  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [cycles, setCycles] = useState<ProductionCycle[]>([]);
  const [products, setProducts] = useState<ProductionItem[]>([]);
  const [productionPlanId, setProductionPlanId] = useState<string>("");
  const [productQuantities, setProductQuantities] = useState<
    Record<string, Record<string, number>>
  >({});
  const [initialQuantities, setInitialQuantities] = useState<
    Record<string, Record<string, number>>
  >({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTier, setSelectedTier] = useState("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<ProductionCycle | null>(
    null,
  );
  const [newCycleData, setNewCycleData] = useState<CycleFormData>({
    displayName: "",
    startTime: "",
    endTime: "",
  });
  const [editCycleData, setEditCycleData] = useState<CycleFormData>({
    displayName: "",
    startTime: "",
    endTime: "",
  });

  // Use templateSaveToDate if in template mode, otherwise use displayDate for saving
  const [saveToDate, setSaveToDate] = useState<string>(
    templateMode && templateSaveToDate
      ? templateSaveToDate
      : singleDateMode && displayDate
        ? displayDate
        : getTodayDate(),
  );

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  const restaurantName = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.name,
  );

  // Set saveToDate appropriately based on mode
  useEffect(() => {
    if (templateMode && templateSaveToDate) {
      setSaveToDate(templateSaveToDate);
    } else if (singleDateMode && displayDate) {
      setSaveToDate(displayDate);
    }
  }, [templateMode, singleDateMode, templateSaveToDate, displayDate]);

  // Fetch data when restaurantId or date changes
  useEffect(() => {
    if (restaurantId && displayDate) {
      const formattedDate = formatDateForAPISimple(displayDate);
      triggerGetProductionCycleBreakdown({
        restaurantId,
        date: formattedDate,
      });
    }
  }, [triggerGetProductionCycleBreakdown, restaurantId, displayDate]);

  // Update local state when API data changes
  useEffect(() => {
    if (apiData) {
      // Check if cycles and items exist and are not null
      const apiCycles = apiData.cycles || [];
      const apiItems = apiData.items || [];

      // Set the productionPlanId from API response
      if (apiData.productionPlanId) {
        setProductionPlanId(apiData.productionPlanId);
      }

      // Transform API cycles to include status
      const transformedCycles: ProductionCycle[] = apiCycles.map(
        (cycle, index) => ({
          cycleId: cycle.cycleId,
          displayName: cycle.displayName,
          startTime: formatTimeFromISO(cycle.startTime),
          endTime: formatTimeFromISO(cycle.endTime),
        }),
      );

      setCycles(transformedCycles);
      setProducts(apiItems);

      // Initialize product quantities from API data using unique key
      const initialQuantitiesData: Record<string, Record<string, number>> = {};
      const productQuantitiesData: Record<string, Record<string, number>> = {};

      apiItems.forEach((item) => {
        const uniqueKey = getProductUniqueKey(item.itemId, item.portionSize);
        // Ensure quantities is not null
        initialQuantitiesData[uniqueKey] = { ...(item.quantities || {}) };
        productQuantitiesData[uniqueKey] = { ...(item.quantities || {}) };
      });

      setInitialQuantities(initialQuantitiesData);
      setProductQuantities(productQuantitiesData);

      // Show info toast based on mode
      if (templateMode && templateSourceDate && isShowingLoadFromDate) {
        toast.info(
          `Template: Showing cycles from ${templateSourceDate} for ${displayDate}. You can edit and save to ${saveToDate}.`,
          { autoClose: 3000 },
        );
      } else if (singleDateMode && displayDate !== getTodayDate()) {
        toast.info(
          `Showing cycles for ${displayDate}. You can edit and save to this date.`,
          { autoClose: 3000 },
        );
      }
    }
  }, [
    apiData,
    templateMode,
    singleDateMode,
    templateSourceDate,
    displayDate,
    saveToDate,
    isShowingLoadFromDate,
  ]);

  const handlePrint = () => {
    if (!productionPlanId) {
      toast.warning("No production plan data available to print.");
      return;
    }

    // Determine which date to use for printing
    let printDate: string;

    if (templateMode) {
      // In template mode, use saveToDate (template target date)
      printDate = saveToDate;
    } else if (singleDateMode) {
      // In single date mode, use the displayDate (historical date)
      printDate = displayDate || saveToDate;
    } else {
      // In normal mode, use displayDate (should be today after reset)
      printDate = displayDate || getTodayDate();
    }

    // Prepare data for printing
    const printData: CycleBreakdownPrintData = {
      date: printDate,
      cycles: cycles,
      items: products,
      productQuantities: productQuantities,
      totalPlanned: products.reduce(
        (sum, product) => sum + (product.prodPlanQty || 0),
        0,
      ),
      restaurantId: restaurantId,
      restaurantName: restaurantName,
      sourceDate:
        templateMode && templateSourceDate ? templateSourceDate : undefined,
      isTemplateMode: templateMode,
      isSourceMode: templateMode && isShowingLoadFromDate,
    };

    printCycleBreakdown(printData);
  };

  const openAddModal = () => {
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setNewCycleData({
      displayName: "",
      startTime: "",
      endTime: "",
    });
  };

  const openEditModal = (cycle: ProductionCycle) => {
    setEditingCycle(cycle);
    setEditCycleData({
      displayName: cycle.displayName,
      startTime: cycle.startTime,
      endTime: cycle.endTime,
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCycle(null);
    setEditCycleData({
      displayName: "",
      startTime: "",
      endTime: "",
    });
  };

  const formatTimeToISO = (time: string, date: string): string => {
    const [year, month, day] = date.split("-");
    return `${year}-${month}-${day}T${time}:00Z`;
  };

  const handleAddCycle = async () => {
    if (
      !newCycleData.displayName ||
      !newCycleData.startTime ||
      !newCycleData.endTime
    ) {
      alert("Please fill all fields");
      return;
    }

    // Check if productionPlanId is available
    if (!productionPlanId) {
      toast.error("Production Plan ID is not available. Please try again.");
      return;
    }

    try {
      // Format the date for API
      const formattedDate = formatDateForAPISimple(saveToDate);
      const formattedDisplayName = newCycleData.displayName.replace(
        /\s+/g,
        "_",
      );

      const payload = {
        displayName: formattedDisplayName,
        restaurantId: restaurantId,
        planId: productionPlanId,
        startTime: formatTimeToISO(newCycleData.startTime, saveToDate),
        endTime: formatTimeToISO(newCycleData.endTime, saveToDate),
        date: formattedDate,
      };

      const response = await createProductionCycle(payload).unwrap();

      if (response.success) {
        // Refresh the data from API using the saveToDate
        await triggerGetProductionCycleBreakdown({
          restaurantId,
          date: formattedDate,
        }).unwrap();

        closeAddModal();
        toast.success(
          templateMode
            ? `Cycle added successfully for template date: ${saveToDate}!`
            : singleDateMode && displayDate !== getTodayDate()
              ? `Cycle added successfully for ${displayDate}!`
              : `Cycle added successfully for ${saveToDate}!`,
        );
      } else {
        toast.error(
          response.message ||
            "Failed to add cycle. Duplicate cycle name might be the cause.",
        );
      }
    } catch (error: any) {
      console.error("Error adding cycle:", error);
      toast.error(
        error?.data?.message ||
          "Failed to add cycle. Duplicate cycle name might be the cause.",
      );
    }
  };

  const handleUpdateCycle = async () => {
    if (
      !editingCycle ||
      !editCycleData.displayName ||
      !editCycleData.startTime ||
      !editCycleData.endTime
    ) {
      alert("Please fill all fields");
      return;
    }

    // Check if productionPlanId is available
    if (!productionPlanId) {
      toast.error("Production Plan ID is not available. Please try again.");
      return;
    }

    try {
      // Format the date for API
      const formattedDate = formatDateForAPISimple(saveToDate);
      const formattedDisplayName = editCycleData.displayName.replace(
        /\s+/g,
        "_",
      );

      const payload = {
        displayName: formattedDisplayName,
        restaurantId: restaurantId,
        planId: productionPlanId,
        startTime: formatTimeToISO(editCycleData.startTime, saveToDate),
        endTime: formatTimeToISO(editCycleData.endTime, saveToDate),
        cycleId: editingCycle.cycleId,
        date: formattedDate,
      };

      const response = await createProductionCycle(payload).unwrap();

      if (response.success) {
        // Refresh the data from API using the saveToDate
        await triggerGetProductionCycleBreakdown({
          restaurantId,
          date: formattedDate,
        }).unwrap();

        closeEditModal();
        toast.success(
          templateMode
            ? `Cycle updated successfully for template date: ${saveToDate}!`
            : singleDateMode && displayDate !== getTodayDate()
              ? `Cycle updated successfully for ${displayDate}!`
              : `Cycle updated successfully for ${saveToDate}!`,
        );
      } else {
        toast.error(response.message || "Failed to update cycle");
      }
    } catch (error: any) {
      console.error("Error updating cycle:", error);
      toast.error(
        error?.data?.message ||
          "Failed to update cycle.Duplicate cycle name might be the cause.",
      );
    }
  };

  // Handle delete cycle with confirmation
  const handleDeleteCycle = async (cycle: ProductionCycle) => {
    // Don't allow deletion if only one cycle remains
    if (cycles.length <= 1) {
      Swal.fire({
        title: "Cannot Delete",
        text: "You must have at least one production cycle.",
        icon: "warning",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const result = await Swal.fire({
      title: templateMode
        ? "Delete Cycle from Template?"
        : singleDateMode
          ? "Delete Cycle from Date?"
          : "Delete Cycle?",
      text: `Are you sure you want to delete the cycle "${cycle.displayName}" from ${saveToDate}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const response = await deleteProductionCycle({
            restaurantId: restaurantId,
            productionCycleId: cycle.cycleId,
          }).unwrap();

          if (!response.success) {
            throw new Error(response.message || "Failed to delete cycle");
          }

          return response;
        } catch (error: any) {
          Swal.showValidationMessage(
            `Failed to delete: ${error?.message || "Unknown error"}`,
          );
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      // Refresh data from API to get updated cycles
      const formattedDate = formatDateForAPISimple(saveToDate);
      await triggerGetProductionCycleBreakdown({
        restaurantId,
        date: formattedDate,
      }).unwrap();

      toast.success(
        templateMode
          ? `Cycle "${cycle.displayName}" deleted from template date ${saveToDate} successfully!`
          : singleDateMode && displayDate !== getTodayDate()
            ? `Cycle "${cycle.displayName}" deleted from ${displayDate} successfully!`
            : `Cycle "${cycle.displayName}" deleted from ${saveToDate} successfully!`,
      );
    }
  };

  // Update the handleSaveAllocations function to include items with 0 quantity
  const handleSaveAllocations = async () => {
    if (!restaurantId) {
      toast.error("Restaurant ID is required");
      return;
    }

    // Check if productionPlanId is available
    if (!productionPlanId) {
      toast.error(
        "Production Plan ID is not available. Please refresh the page.",
      );
      return;
    }

    try {
      // Prepare the request payload
      const payload: CreateProductionCyclesRequest = {
        cycles: [],
      };

      // Group quantities by cycleId
      const cycleQuantities: Record<string, ProductionCycleRequest> = {};

      // Initialize cycles with their data
      cycles.forEach((cycle) => {
        cycleQuantities[cycle.cycleId] = {
          restaurantId: restaurantId,
          cycleId: cycle.cycleId,
          items: [],
        };
      });

      Object.keys(productQuantities).forEach((uniqueKey) => {
        const [itemId, portionSize] = uniqueKey.split("_");

        // Find the product to get its details
        const product = products.find(
          (p) => getProductUniqueKey(p.itemId, p.portionSize) === uniqueKey,
        );

        const cycleData = productQuantities[uniqueKey];

        Object.keys(cycleData).forEach((cycleId) => {
          const quantity = cycleData[cycleId] || 0; // Get quantity (could be 0)

          // Check if cycle exists
          if (cycleQuantities[cycleId]) {
            // Check if this item already exists in the cycle
            const existingItemIndex = cycleQuantities[cycleId].items.findIndex(
              (item) =>
                item.itemId === itemId && item.portionSize === portionSize,
            );

            if (existingItemIndex !== -1) {
              // If item exists, update its quantity (even if it's 0)
              cycleQuantities[cycleId].items[existingItemIndex].quantity =
                quantity;
            } else {
              const newItem: ProductionCycleItemRequest = {
                itemId: itemId,
                productId: product?.productId || "",
                portionSize: portionSize as "REGULAR" | "LARGE" | "SMALL",
                quantity: quantity,
                displayName: product?.displayName || "",
                classFilterTag: product?.classFilterTag || "No tier",
              };
              cycleQuantities[cycleId].items.push(newItem);
            }
          }
        });
      });

      products.forEach((product) => {
        const uniqueKey = getProductUniqueKey(
          product.itemId,
          product.portionSize,
        );

        cycles.forEach((cycle) => {
          // Check if this product exists for this cycle
          const existingCycleData = productQuantities[uniqueKey] || {};
          const quantity = existingCycleData[cycle.cycleId] || 0;

          // Find or create the cycle in cycleQuantities
          if (!cycleQuantities[cycle.cycleId]) {
            cycleQuantities[cycle.cycleId] = {
              restaurantId: restaurantId,
              cycleId: cycle.cycleId,
              items: [],
            };
          }

          // Check if this product already exists in this cycle
          const existingItemIndex = cycleQuantities[
            cycle.cycleId
          ].items.findIndex(
            (item) =>
              item.itemId === product.itemId &&
              item.portionSize === product.portionSize,
          );

          // Only add if not already exists (to avoid duplicates)
          if (existingItemIndex === -1) {
            const newItem: ProductionCycleItemRequest = {
              itemId: product.itemId,
              productId: product.productId || "",
              portionSize: product.portionSize as "REGULAR" | "LARGE" | "SMALL",
              quantity: quantity, // This can be 0
              displayName: product.displayName || "",
              classFilterTag: product.classFilterTag || "No tier",
            };
            cycleQuantities[cycle.cycleId].items.push(newItem);
          }
        });
      });

      payload.cycles = Object.values(cycleQuantities);

      // Check if there are any cycles to save
      const totalItems = payload.cycles.reduce(
        (sum, cycle) => sum + cycle.items.length,
        0,
      );
      if (totalItems === 0) {
        toast.info("No items to save");
        return;
      }

      const formattedDate = formatDateForAPISimple(saveToDate);

      // Restructure cycles to include restaurantId and date
      const cyclesWithRestaurantId = payload.cycles.map((cycle) => ({
        ...cycle,
        restaurantId,
        date: formattedDate,
      }));

      const response = await createProductionCycles({
        cycles: cyclesWithRestaurantId,
      }).unwrap();

      if (response.success) {
        // Update initial quantities to current quantities
        setInitialQuantities({ ...productQuantities });

        // Refresh the data from API using saveToDate
        await triggerGetProductionCycleBreakdown({
          restaurantId,
          date: formattedDate,
        }).unwrap();

        toast.success(
          templateMode
            ? `Allocations saved successfully for template date: ${saveToDate}!`
            : singleDateMode && displayDate !== getTodayDate()
              ? `Allocations saved successfully for ${displayDate}!`
              : `Allocations saved successfully for ${saveToDate}!`,
        );
      } else {
        toast.error(response.message || "Failed to save allocations");
      }
    } catch (error: any) {
      console.error("Error saving allocations:", error);
      toast.error(
        error?.data?.message || "Failed to save allocations. Please try again.",
      );
    }
  };

  // Calculate remaining quantity for a product
  const getRemainingQuantity = (
    itemId: string,
    portionSize: string,
  ): number => {
    const product = products.find(
      (p) => p.itemId === itemId && p.portionSize === portionSize,
    );
    if (!product) return 0;

    const planned = product.prodPlanQty || 0;
    const allocated = getProductTotal(itemId, portionSize);
    return Math.max(0, planned - allocated);
  };

  // Check if input is valid (not exceeding remaining quantity)
  const isValidQuantity = (
    itemId: string,
    portionSize: string,
    cycleId: string,
    newValue: number,
  ): boolean => {
    // Get current total for this product across all cycles
    const currentTotal = getProductTotal(itemId, portionSize);

    // Get current value for this specific cycle
    const uniqueKey = getProductUniqueKey(itemId, portionSize);
    const currentValue = productQuantities[uniqueKey]?.[cycleId] || 0;

    // Calculate what the new total would be
    const newTotal = currentTotal - currentValue + newValue;

    // Get the product's planned quantity
    const product = products.find(
      (p) => p.itemId === itemId && p.portionSize === portionSize,
    );
    const plannedQuantity = product?.prodPlanQty || 0;

    // Return true if new total doesn't exceed planned quantity
    return newTotal <= plannedQuantity;
  };

  const updateQuantity = (
    itemId: string,
    portionSize: string,
    cycleId: string,
    quantity: number,
  ) => {
    // Validate input
    if (quantity < 0) {
      toast.warning("Quantity cannot be negative");
      return;
    }

    const uniqueKey = getProductUniqueKey(itemId, portionSize);
    const currentValue = productQuantities[uniqueKey]?.[cycleId] || 0;

    // If value hasn't changed, do nothing
    if (currentValue === quantity) return;

    // Check if the new value is valid
    if (!isValidQuantity(itemId, portionSize, cycleId, quantity)) {
      const remaining = getRemainingQuantity(itemId, portionSize);
      toast.warning(
        `Cannot allocate more than ${
          remaining + currentValue
        } items for this product`,
      );
      return;
    }

    setProductQuantities((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...prev[uniqueKey],
        [cycleId]: quantity,
      },
    }));
  };

  const getProductTotal = (itemId: string, portionSize: string) => {
    const uniqueKey = getProductUniqueKey(itemId, portionSize);
    const cycleQuantities = productQuantities[uniqueKey] || {};
    return Object.values(cycleQuantities).reduce(
      (sum, qty) => sum + (qty || 0),
      0,
    );
  };

  const getCycleTotal = (cycleId: string) => {
    return Object.values(productQuantities).reduce((sum, cycleData) => {
      return sum + ((cycleData && cycleData[cycleId]) || 0);
    }, 0);
  };

  const getTotalProduction = () => {
    return Object.values(productQuantities).reduce((sum, cycleData) => {
      if (!cycleData) return sum;
      return (
        sum +
        Object.values(cycleData).reduce(
          (cycleSum, qty) => cycleSum + (qty || 0),
          0,
        )
      );
    }, 0);
  };

  const getProductVariance = (itemId: string, portionSize: string) => {
    const product = products.find(
      (p) => p.itemId === itemId && p.portionSize === portionSize,
    );
    if (!product) return 0;

    const planned = product.prodPlanQty || 0;
    const allocated = getProductTotal(itemId, portionSize);
    return planned - allocated;
  };

  const getVarianceStatus = (variance: number) => {
    if (variance === 0) return { text: "Complete", color: "success" };
    if (variance > 0)
      return { text: `${variance} remaining`, color: "warning" };
    return { text: `${Math.abs(variance)} over`, color: "destructive" };
  };

  const shouldDisableInputs = isPastDate && singleDateMode;

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    const initialKeys = Object.keys(initialQuantities);
    const currentKeys = Object.keys(productQuantities);

    if (initialKeys.length !== currentKeys.length) return true;

    for (const key of initialKeys) {
      const initial = initialQuantities[key];
      const current = productQuantities[key];

      if (!current) return true;

      const cycleIds = new Set([
        ...Object.keys(initial),
        ...Object.keys(current),
      ]);

      for (const cycleId of cycleIds) {
        if ((initial[cycleId] || 0) !== (current[cycleId] || 0)) {
          return true;
        }
      }
    }

    return false;
  }, [initialQuantities, productQuantities]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (selectedTier !== "all") {
      // Filter by classFilterTag property
      filtered = filtered.filter((p) => {
        const tier = getProductTier(p);
        return tier === selectedTier.toLowerCase();
      });
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (p) =>
          (p.displayName || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (p.itemId || "").toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    return filtered;
  }, [searchQuery, selectedTier, products]);

  return (
    <div
      className={`cycle-breakdown-screen ${
        templateMode
          ? "template-mode"
          : singleDateMode
            ? "single-date-mode"
            : ""
      }`}
    >
      <div className="cycle-breakdown__header">
        <div>
          <h1 className="cycle-breakdown__title">
            Production Cycle Breakdown (UC-P2)
            {templateMode ? (
              <Badge color="info" className="ms-2">
                <Copy size={12} className="me-1" />
                Template Mode
                {isShowingLoadFromDate ? " (Source)" : ""}
              </Badge>
            ) : singleDateMode ? (
              <Badge color="warning" className="ms-2">
                <EyeOff size={12} className="me-1" />
                Viewing Date
              </Badge>
            ) : null}
          </h1>
          <div className="date-display-info">
            {templateMode ? (
              <div className="template-info">
                <span className="text-info">
                  <i className="fas fa-copy me-1"></i>
                  Template Mode:{" "}
                  {isShowingLoadFromDate
                    ? "Showing source data from"
                    : "Showing target data for"}{" "}
                  <strong>{displayDate}</strong>
                  {saveToDate !== displayDate && (
                    <span className="ms-2">
                      • Save will create allocations for:{" "}
                      <strong>{saveToDate}</strong>
                    </span>
                  )}
                </span>
              </div>
            ) : singleDateMode ? (
              <div className="single-date-info">
                <span className="text-warning">
                  <i className="fas fa-calendar me-1"></i>
                  Viewing date: <strong>{displayDate}</strong>
                  {saveToDate !== displayDate && (
                    <span className="ms-2">
                      • Save will create allocations for:{" "}
                      <strong>{saveToDate}</strong>
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <p className="cycle-breakdown__subtitle">
                Showing data for: <strong>{displayDate}</strong>
              </p>
            )}
          </div>
        </div>
        <div className="cycle-breakdown__header-actions">
          {/* Print Button */}
          <Button
            onClick={handlePrint}
            className="cycle-breakdown__print-btn me-2"
            disabled={
              isPrinting ||
              !productionPlanId ||
              cycles.length === 0 ||
              products.length === 0 ||
              shouldDisableInputs
            }
            color="secondary"
            title={
              shouldDisableInputs
                ? "Cannot print past dates"
                : !productionPlanId
                  ? "No production plan data available"
                  : cycles.length === 0
                    ? "No cycles available"
                    : products.length === 0
                      ? "No products available"
                      : `Print cycle breakdown for ${saveToDate}`
            }
          >
            {isPrinting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Printing...
              </>
            ) : (
              <>
                <Printer size={14} className="button__icon" />
                Print
              </>
            )}
          </Button>

          {/* Add Cycle Button */}
          <Button
            onClick={openAddModal}
            className="cycle-breakdown__add-cycle-btn"
            disabled={
              isLoading || isDeleting || isSaving || shouldDisableInputs
            }
            title={
              shouldDisableInputs
                ? "Cannot add cycles to past dates"
                : templateMode
                  ? `Add cycle for template date: ${saveToDate}`
                  : singleDateMode
                    ? `Add cycle for ${displayDate}`
                    : ""
            }
          >
            <Plus className="button__icon" />
            {shouldDisableInputs ? "View Only" : "Add Cycle"}
          </Button>
        </div>
      </div>

      <div className="cycle-breakdown__filters">
        <div className="cycle-breakdown__search-wrapper">
          <Input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cycle-breakdown__search-input"
            disabled={isLoading || isDeleting || isSaving || isCreating}
          />
        </div>
        <Button
          onClick={handleSaveAllocations}
          disabled={
            isLoading ||
            isSaving ||
            isDeleting ||
            isCreating ||
            !hasUnsavedChanges ||
            !productionPlanId ||
            shouldDisableInputs
          }
          color={
            hasUnsavedChanges
              ? templateMode
                ? "info"
                : singleDateMode
                  ? "warning"
                  : "primary"
              : "secondary"
          }
          className="cycle-breakdown__save-btn"
          title={
            shouldDisableInputs
              ? "Cannot modify past dates"
              : templateMode
                ? `Save allocations to template date: ${saveToDate}`
                : singleDateMode
                  ? `Save allocations to ${displayDate}`
                  : `Save allocations to ${saveToDate}`
          }
        >
          {shouldDisableInputs ? (
            <>
              <Eye size={14} className="button__icon" />
              View Only
            </>
          ) : isSaving ? (
            <>
              <Spinner size="sm" className="button__icon me-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="button__icon" />
              {templateMode
                ? `Save to ${saveToDate}`
                : singleDateMode
                  ? `Save to ${saveToDate}`
                  : hasUnsavedChanges
                    ? `Save to ${saveToDate}`
                    : "Saved"}
            </>
          )}
        </Button>
        <div className="cycle-breakdown__tier-filters">
          <button
            onClick={() => setSelectedTier("all")}
            className={`cycle-breakdown__tier-filter-btn ${
              selectedTier === "all"
                ? "cycle-breakdown__tier-filter-btn--active"
                : ""
            }`}
            disabled={isLoading || isDeleting || isSaving || isCreating}
          >
            All
          </button>
          {["gold", "bronze", "platinum"].map((tier) => (
            <button
              key={tier}
              onClick={() => setSelectedTier(tier)}
              className={`cycle-breakdown__tier-filter-btn ${
                selectedTier === tier
                  ? "cycle-breakdown__tier-filter-btn--active"
                  : ""
              }`}
              disabled={isLoading || isDeleting || isSaving || isCreating}
            >
              {tierIcons[tier]} {tierDisplayNames[tier]}
            </button>
          ))}
        </div>
      </div>

      {!productionPlanId && !isLoading && !error && (
        <Alert color="warning" className="cycle-breakdown__warning-alert">
          <div className="cycle-breakdown__warning-content">
            <strong>Production Plan ID not available.</strong>
            <div className="cycle-breakdown__warning-message">
              Please refresh the page or contact support.
            </div>
          </div>
        </Alert>
      )}

      {/* Table Container with Loading Overlay */}
      <div
        className={`cycle-breakdown__grid-container ${
          isLoading || isDeleting || isSaving || isCreating
            ? "cycle-breakdown__grid-container--loading"
            : ""
        }`}
      >
        {/* Loading Overlay */}
        {(isLoading || isDeleting || isSaving || isCreating) && (
          <div className="cycle-breakdown__loading-overlay">
            <LoadingMessage />
          </div>
        )}

        <table className="cycle-breakdown__table">
          <thead>
            <tr>
              <th className="cycle-breakdown__table-header--product">
                Product
              </th>
              {cycles.map((cycle, index) => {
                const colors = getCycleColor(cycle.cycleId, index);

                return (
                  <th
                    key={cycle.cycleId}
                    className={`cycle-breakdown__table-header--cycle ${colors.bg}`}
                  >
                    <div className="cycle-breakdown__table-header-content">
                      <div>
                        <div
                          className={`cycle-breakdown__cycle-name ${colors.text}`}
                        >
                          {cycle.displayName}
                          {(templateMode || singleDateMode) && (
                            <Badge
                              color={templateMode ? "info" : "warning"}
                              className="ms-2"
                              size="sm"
                            >
                              {templateMode
                                ? isShowingLoadFromDate
                                  ? "Source"
                                  : "Template"
                                : "Viewing"}
                            </Badge>
                          )}
                        </div>
                        <div className="cycle-breakdown__cycle-time">
                          <Clock className="cycle-breakdown__time-icon" />
                          {cycle.startTime} - {cycle.endTime}
                        </div>
                      </div>
                      <div className="cycle-breakdown__cycle-actions">
                        <button
                          onClick={() => openEditModal(cycle)}
                          className="cycle-breakdown__edit-cycle-btn"
                          title="Edit cycle"
                          disabled={
                            isLoading ||
                            isDeleting ||
                            isSaving ||
                            isCreating ||
                            !productionPlanId
                          }
                        >
                          <Eye className="cycle-breakdown__edit-icon" />
                        </button>
                        {cycles.length > 1 && (
                          <button
                            onClick={() => handleDeleteCycle(cycle)}
                            className="cycle-breakdown__remove-cycle-btn"
                            title={
                              templateMode
                                ? `Delete cycle from template date: ${saveToDate}`
                                : singleDateMode
                                  ? `Delete cycle from ${displayDate}`
                                  : `Delete cycle from ${saveToDate}`
                            }
                            disabled={
                              isLoading || isDeleting || isSaving || isCreating
                            }
                          >
                            {isDeleting ? (
                              <Spinner
                                size="sm"
                                className="cycle-breakdown__trash-icon"
                              />
                            ) : (
                              <Trash2 className="cycle-breakdown__trash-icon" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </th>
                );
              })}
              <th className="cycle-breakdown__table-header--planned">
                Planned Total
              </th>
              <th className="cycle-breakdown__table-header--status">Status</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td
                  colSpan={(cycles.length || 0) + 3}
                  className="text-center py-5"
                >
                  <div className="text-danger">
                    There is no production plan for given date. Please create a
                    production plan first.
                  </div>
                </td>
              </tr>
            ) : !productionPlanId ? (
              <tr>
                <td
                  colSpan={(cycles.length || 0) + 3}
                  className="text-center py-5"
                >
                  <div className="text-warning">
                    Production Plan ID is missing. Please refresh the page.
                  </div>
                </td>
              </tr>
            ) : filteredProducts.length > 0 ? (
              filteredProducts.map((product, idx) => {
                const productTier = getProductTier(product);
                const remainingQty = getRemainingQuantity(
                  product.itemId,
                  product.portionSize,
                );

                return (
                  <tr
                    key={getProductUniqueKey(
                      product.itemId,
                      product.portionSize,
                    )}
                    className={`cycle-breakdown__table-row--product ${
                      idx % 2 === 0
                        ? "cycle-breakdown__table-row--product:nth-child(even)"
                        : "cycle-breakdown__table-row--product:nth-child(odd)"
                    } ${
                      templateMode
                        ? "template-mode"
                        : singleDateMode
                          ? "single-date-mode"
                          : ""
                    }`}
                  >
                    <td className="cycle-breakdown__table-cell--product">
                      <div className="cycle-breakdown__product-info">
                        <span className="cycle-breakdown__tier-icon">
                          {tierIcons[productTier]}
                        </span>
                        <div>
                          <div className="cycle-breakdown__product-name">
                            {product.displayName || "Unnamed Product"}
                            {(templateMode || singleDateMode) &&
                              product.prodPlanQty &&
                              product.prodPlanQty > 0 && (
                                <Badge
                                  color={templateMode ? "info" : "warning"}
                                  className="ms-2"
                                  size="sm"
                                >
                                  {templateMode
                                    ? isShowingLoadFromDate
                                      ? "Source"
                                      : "Template"
                                    : "Viewing"}
                                </Badge>
                              )}
                          </div>
                          <div className="cycle-breakdown__product-details">
                            <div className="cycle-breakdown__product-portion">
                              {product.portionSize || "N/A"} •{" "}
                              {tierDisplayNames[productTier]}
                            </div>
                            <div className="cycle-breakdown__remaining-info">
                              Remaining:{" "}
                              <span className="cycle-breakdown__remaining-value">
                                {remainingQty}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    {cycles.map((cycle, cycleIndex) => {
                      const uniqueKey = getProductUniqueKey(
                        product.itemId,
                        product.portionSize,
                      );
                      const currentValue =
                        productQuantities[uniqueKey]?.[cycle.cycleId] || 0;
                      const maxValue = currentValue + remainingQty;

                      return (
                        <td
                          key={cycle.cycleId}
                          className={getCycleCellClass(cycleIndex)}
                        >
                          <div className="cycle-breakdown__input-wrapper">
                            <Input
                              type="number"
                              min="0"
                              max={maxValue}
                              placeholder="0"
                              value={currentValue || ""}
                              onChange={(e) => {
                                if (shouldDisableInputs) return;
                                const value = parseInt(e.target.value) || 0;
                                updateQuantity(
                                  product.itemId,
                                  product.portionSize,
                                  cycle.cycleId,
                                  value,
                                );
                              }}
                              onBlur={(e) => {
                                if (shouldDisableInputs) return;
                                if (e.target.value === "") {
                                  updateQuantity(
                                    product.itemId,
                                    product.portionSize,
                                    cycle.cycleId,
                                    0,
                                  );
                                }
                              }}
                              onWheel={(e) => e.currentTarget.blur()}
                              className={`cycle-breakdown__cycle-input ${
                                templateMode
                                  ? "template-input"
                                  : singleDateMode
                                    ? "single-date-input"
                                    : ""
                              } ${shouldDisableInputs ? "cycle-breakdown__cycle-input--disabled" : ""}`}
                              disabled={
                                isLoading ||
                                isDeleting ||
                                isSaving ||
                                isCreating ||
                                !productionPlanId ||
                                shouldDisableInputs
                              }
                            />
                          </div>
                        </td>
                      );
                    })}
                    <td className="cycle-breakdown__table-cell--planned">
                      <Badge className="cycle-breakdown__planned-badge">
                        {product.prodPlanQty || 0}
                      </Badge>
                    </td>
                    <td className="cycle-breakdown__table-cell--status">
                      {(() => {
                        const variance = getProductVariance(
                          product.itemId,
                          product.portionSize,
                        );
                        const status = getVarianceStatus(variance);
                        return (
                          <div
                            className={`cycle-breakdown__variance-badge cycle-breakdown__variance-badge--${status.color}`}
                          >
                            {status.text}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={(cycles.length || 0) + 3}
                  className="text-center py-4"
                >
                  There are no cycles for {displayDate}. Please create cycles
                  first.
                  {templateMode &&
                    templateSourceDate &&
                    displayDate !== templateSourceDate && (
                      <div className="mt-2 text-info small">
                        Template Mode:{" "}
                        {isShowingLoadFromDate
                          ? "Showing source data from"
                          : "Showing target data for"}{" "}
                        <strong>{displayDate}</strong>
                      </div>
                    )}
                  {singleDateMode && displayDate !== getTodayDate() && (
                    <div className="mt-2 text-warning small">
                      Viewing Date: Showing data for{" "}
                      <strong>{displayDate}</strong>
                    </div>
                  )}
                </td>
              </tr>
            )}
            <tr className="cycle-breakdown__table-row--totals">
              <td className="cycle-breakdown__table-cell--total-label">
                Cycle Total
              </td>
              {cycles.map((cycle, index) => {
                const colors = getCycleColor(cycle.cycleId, index);
                const cycleTotal = getCycleTotal(cycle.cycleId);
                return (
                  <td
                    key={cycle.cycleId}
                    className={getCycleTotalCellClass(index)}
                  >
                    <div
                      className={`cycle-breakdown__cycle-total-badge ${colors.badge}`}
                    >
                      {cycleTotal}
                    </div>
                  </td>
                );
              })}
              <td className="cycle-breakdown__table-cell--planned-total">
                <div className="cycle-breakdown__planned-total-badge">
                  {products.reduce(
                    (sum, product) => sum + (product.prodPlanQty || 0),
                    0,
                  )}
                </div>
              </td>
              <td className="cycle-breakdown__table-cell--status-total">
                <div className="cycle-breakdown__status-total-badge">
                  {(() => {
                    const totalPlanned = products.reduce(
                      (sum, product) => sum + (product.prodPlanQty || 0),
                      0,
                    );
                    const totalAllocated = getTotalProduction();
                    const variance = totalPlanned - totalAllocated;
                    return variance === 0 ? "✓ Complete" : `${variance} left`;
                  })()}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!isLoading &&
        !error &&
        !isDeleting &&
        !isSaving &&
        !isCreating &&
        productionPlanId && (
          <div className="cycle-breakdown__summary-cards">
            <div className="cycle-breakdown__summary-card cycle-breakdown__summary-card--cycles">
              <div className="cycle-breakdown__summary-card-label">
                Total Cycles
              </div>
              <div className="cycle-breakdown__summary-card-value">
                {cycles.length}
              </div>
            </div>
            <div className="cycle-breakdown__summary-card cycle-breakdown__summary-card--production">
              <div className="cycle-breakdown__summary-card-label">
                Total Production
              </div>
              <div className="cycle-breakdown__summary-card-value">
                {getTotalProduction()}
              </div>
              <div className="cycle-breakdown__summary-card-unit">packs</div>
            </div>
            <div className="cycle-breakdown__summary-card cycle-breakdown__summary-card--products">
              <div className="cycle-breakdown__summary-card-label">
                Products
              </div>
              <div className="cycle-breakdown__summary-card-value">
                {filteredProducts.length}
              </div>
            </div>
            <div className="cycle-breakdown__summary-card cycle-breakdown__summary-card--average">
              <div className="cycle-breakdown__summary-card-label">
                Avg per Cycle
              </div>
              <div className="cycle-breakdown__summary-card-value">
                {cycles.length > 0
                  ? Math.round(getTotalProduction() / cycles.length)
                  : 0}
              </div>
              <div className="cycle-breakdown__summary-card-unit">packs</div>
            </div>
          </div>
        )}

      {/* Add Cycle Modal */}
      <Modal
        isOpen={isAddModalOpen}
        toggle={closeAddModal}
        className="cycle-add-modal"
      >
        <ModalHeader toggle={closeAddModal}>
          <div className="modal-header-content">
            <Plus className="modal-header-icon" />
            Add New Cycle
            {templateMode ? (
              <Badge color="info" className="ms-2">
                Template Mode
              </Badge>
            ) : singleDateMode ? (
              <Badge color="warning" className="ms-2">
                Viewing Date
              </Badge>
            ) : null}
          </div>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="displayName">Display Name *</Label>
            <Input
              type="text"
              id="displayName"
              placeholder="Enter cycle display name"
              value={newCycleData.displayName}
              onChange={(e) =>
                setNewCycleData({
                  ...newCycleData,
                  displayName: e.target.value,
                })
              }
              required
            />
          </FormGroup>
          <div className="time-inputs-row">
            <FormGroup className="time-input-group">
              <Label for="startTime">Start Time *</Label>
              <Input
                type="time"
                id="startTime"
                value={newCycleData.startTime}
                onChange={(e) =>
                  setNewCycleData({
                    ...newCycleData,
                    startTime: e.target.value,
                  })
                }
                required
              />
            </FormGroup>
            <FormGroup className="time-input-group">
              <Label for="endTime">End Time *</Label>
              <Input
                type="time"
                id="endTime"
                value={newCycleData.endTime}
                onChange={(e) =>
                  setNewCycleData({ ...newCycleData, endTime: e.target.value })
                }
                required
              />
            </FormGroup>
          </div>
          <div className="date-info">
            <small className="text-muted">
              {templateMode && (
                <>
                  <br />
                  <span className="text-info">
                    Template Mode: Cycle will be added to template date
                  </span>
                </>
              )}
              {singleDateMode && displayDate !== getTodayDate() && (
                <>
                  <br />
                  <span className="text-warning">
                    Viewing Date Mode: Cycle will be added to {displayDate}
                  </span>
                </>
              )}
              <br />
            </small>
          </div>
          {!productionPlanId && (
            <Alert color="warning" className="mt-3 mb-0">
              Production Plan ID is not available. Please refresh the page and
              try again.
            </Alert>
          )}
          {templateMode && (
            <Alert color="info" className="mt-3 mb-0">
              <strong>Template Mode Active</strong>
              <br />
              You are editing template data for date:{" "}
              <strong>{saveToDate}</strong>
            </Alert>
          )}
          {singleDateMode && displayDate !== getTodayDate() && (
            <Alert color="warning" className="mt-3 mb-0">
              <strong>Viewing Date Mode Active</strong>
              <br />
              You are editing data for date: <strong>{displayDate}</strong>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeAddModal}>
            <X className="button__icon" />
            Cancel
          </Button>
          <Button
            color={
              templateMode ? "info" : singleDateMode ? "warning" : "primary"
            }
            onClick={handleAddCycle}
            disabled={
              !newCycleData.displayName ||
              !newCycleData.startTime ||
              !newCycleData.endTime ||
              isCreating ||
              !productionPlanId
            }
            title={
              templateMode
                ? `Add cycle for template date: ${saveToDate}`
                : singleDateMode
                  ? `Add cycle for ${displayDate}`
                  : `Add cycle for ${saveToDate}`
            }
          >
            {isCreating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              <>
                <Save className="button__icon" />
                {templateMode
                  ? `Add for ${saveToDate}`
                  : singleDateMode
                    ? `Add for ${displayDate}`
                    : `Add for ${saveToDate}`}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Cycle Modal */}
      <Modal
        isOpen={isEditModalOpen}
        toggle={closeEditModal}
        className="cycle-edit-modal"
      >
        <ModalHeader toggle={closeEditModal}>
          <div className="modal-header-content">
            <Eye className="modal-header-icon" />
            Edit Cycle
            {templateMode ? (
              <Badge color="info" className="ms-2">
                Template Mode
              </Badge>
            ) : singleDateMode ? (
              <Badge color="warning" className="ms-2">
                Viewing Date
              </Badge>
            ) : null}
          </div>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="editDisplayName">Display Name *</Label>
            <Input
              type="text"
              id="editDisplayName"
              placeholder="Enter cycle display name"
              value={editCycleData.displayName}
              onChange={(e) =>
                setEditCycleData({
                  ...editCycleData,
                  displayName: e.target.value,
                })
              }
              required
            />
          </FormGroup>
          <div className="time-inputs-row">
            <FormGroup className="time-input-group">
              <Label for="editStartTime">Start Time *</Label>
              <Input
                type="time"
                id="editStartTime"
                value={editCycleData.startTime}
                onChange={(e) =>
                  setEditCycleData({
                    ...editCycleData,
                    startTime: e.target.value,
                  })
                }
                required
              />
            </FormGroup>
            <FormGroup className="time-input-group">
              <Label for="editEndTime">End Time *</Label>
              <Input
                type="time"
                id="editEndTime"
                value={editCycleData.endTime}
                onChange={(e) =>
                  setEditCycleData({
                    ...editCycleData,
                    endTime: e.target.value,
                  })
                }
                required
              />
            </FormGroup>
          </div>
          <div className="date-info">
            <small className="text-muted">
              Date: <strong>{saveToDate}</strong>
              {templateMode && editingCycle && (
                <>
                  <br />
                  <span className="text-info">
                    Editing template for date: {saveToDate}
                  </span>
                </>
              )}
              {singleDateMode && editingCycle && (
                <>
                  <br />
                  <span className="text-warning">
                    Editing data for date: {displayDate}
                  </span>
                </>
              )}
              <br />
            </small>
          </div>
          {!productionPlanId && (
            <Alert color="warning" className="mt-3 mb-0">
              Production Plan ID is not available. Please refresh the page and
              try again.
            </Alert>
          )}
          {templateMode && (
            <Alert color="info" className="mt-3 mb-0">
              <strong>Template Mode</strong>
              <br />
              You are editing template data for date:{" "}
              <strong>{saveToDate}</strong>
            </Alert>
          )}
          {singleDateMode && displayDate !== getTodayDate() && (
            <Alert color="warning" className="mt-3 mb-0">
              <strong>Viewing Date Mode</strong>
              <br />
              You are editing data for date: <strong>{displayDate}</strong>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeEditModal}>
            <X className="button__icon" />
            Cancel
          </Button>
          <Button
            color={
              templateMode ? "info" : singleDateMode ? "warning" : "primary"
            }
            onClick={handleUpdateCycle}
            disabled={
              !editCycleData.displayName ||
              !editCycleData.startTime ||
              !editCycleData.endTime ||
              isCreating ||
              !productionPlanId
            }
            title={
              templateMode
                ? `Update cycle for template date: ${saveToDate}`
                : singleDateMode
                  ? `Update cycle for ${displayDate}`
                  : `Update cycle for ${saveToDate}`
            }
          >
            {isCreating ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <Save className="button__icon" />
                {templateMode
                  ? `Update for ${saveToDate}`
                  : singleDateMode
                    ? `Update for ${displayDate}`
                    : `Update for ${saveToDate}`}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
